import { Box, Container, Grid, Typography } from "@mui/material";
import Image from "next/image";
import { useTranslation } from "next-i18next";

import { Page, Section } from "@larvitu/shared/mui/ui-components";
import { Link } from "@larvitu/shared/next/mui";
import { IconifyIcon } from "@larvitu/shared/react/components/ui-iconify-icon";

import drGeiges from "../../public/assets/images/geiges/859_Dr_Geiges_007.jpg";
import praxisGespraech from "../../public/assets/images/geiges/859_Dr_Geiges_021.jpg";
import besprechung from "../../public/assets/images/geiges/859_Dr_Geiges_030.jpg";
import {
  ConsultingSection,
  ContactUs,
  FAQS,
  HeroComponent,
  QuickHelpComponent,
  SectionStyle,
  ServiceComponent,
} from "../components";

import { getLayout } from "../getLayout";

import { getServerTranslations } from "../lib/i18n/getServerTranslations";

// import { serverSideTranslations } from 'next-i18next/serverSideTranslations'

import type { AppPage } from "../@types/page";
import type { GetStaticProps, InferGetStaticPropsType } from "next";

const email = process.env.NEXT_PUBLIC_NODEMAILER_RECEIVERS ?? ''

const Home: AppPage<InferGetStaticPropsType<typeof getStaticProps>> = () => {
  const { t, ready } = useTranslation(["home", "faq", "common", "contact", "service"]);

  const contactAddress = t("address", { ns: "contact", returnObjects: true });

  return (
    <Page title="home">
      {/* ----------------------------------------------------------------------------- */}
      <Section id="hero">
        <HeroComponent
          greeting={t("main.greeting", { ns: "home" })}
          title={t("main.title", { ns: "home" })}
          specialised={t("main.specialised.sections", {
            ns: "home",
            returnObjects: true,
          })}
          image={drGeiges}
          containedButton={t("contact", { ns: "common" })}
          containedLink="/contact"
          outlinedButton={t("toService", { ns: "common" })}
        />
      </Section>

      <Section id="description">
        <Container>
          <Typography gutterBottom paragraph>
            {t("main.content", { ns: "home" })}
          </Typography>
        </Container>
      </Section>

      {/* ----------------------------------------------------------------------------- */}

      <Section id="doctors">
        <SectionStyle>
          <Container>
            <Typography display={{ md: "none" }} mb={2} fontWeight="bold" gutterBottom variant="h2">
              {t("values.header", { ns: "home" })}
            </Typography>
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              gap={{ xs: 6, lg: 0 }}
              justifyContent={{ md: "space-between" }}
            >
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                width={{ xs: "100%", lg: "55%" }}
                maxWidth={{ md: 500 }}
              >
                <Image
                  src={praxisGespraech}
                  sizes="(max-with: 900px) 100vw, (max-with: 1200px) 50vw, 400px"
                  alt="besprechung"
                  title="besprechung"
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                  }}
                  placeholder="blur"
                />
              </Box>
              <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="center"
                width={{ xs: "100%", lg: "45%" }}
                maxWidth={500}
              >
                <Grid item sm={12} display={{ xs: "none", md: "block" }}>
                  <Typography mb={2} fontWeight="bold" gutterBottom variant="h2">
                    {t("values.header", { ns: "home" })}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography>{t("values.content", { ns: "home" })}</Typography>
                </Grid>
              </Grid>
            </Box>
          </Container>
        </SectionStyle>
      </Section>

      {/* ----------------------------------------------------------------------------- */}

      <Section id="leistungen">
        <ServiceComponent
          header={t("header", { ns: "service" })}
          content={t("content", { ns: "service" })}
          serviceItems={t("serviceItems", {
            ns: "service",
            returnObjects: true,
          })}
          contactButton={t("to-the-contact-form", { ns: "common" })}
        />
      </Section>

      {/* ----------------------------------------------------------------------------- */}

      <Section id="contact">
        <ConsultingSection
          header={
            <Typography
              fontWeight="bold"
              variant="h2"
              sx={{
                color: "common.black",
                mb: {
                  xs: 3,
                  md: 8,
                },
                textAlign: "left",
              }}
            >
              {t("header", { ns: "contact" })}
            </Typography>
          }
          address={
            <Link href="https://goo.gl/maps/EDFtpMVQEVwLUpdn7" color="inherit">
              <Typography>
                {contactAddress.street.name} {contactAddress.street.number},{" "}
                {contactAddress.zipcode} {contactAddress.city}
              </Typography>
            </Link>
          }
          email={
            // <Link color="inherit" href={`mailto:${t("address.email", { ns: "contact" })}`}>
            //   {t("address.email", { ns: "contact" }).split(",")[0].trim()}
            // </Link>
            <Link color="inherit" href={`mailto:${email}`}>
          {email.split(",")[0].trim()}
        </Link>
          }
        />

        <ContactUs
          header={t("arrival", { ns: "contact" })}
          openingHours={t("opening-hours", {
            ns: "contact",
            returnObjects: true,
          })}
          address={t("address", { ns: "contact", returnObjects: true })}
          content={t("content", { ns: "contact", returnObjects: true })}
        />
      </Section>

      {/* ----------------------------------------------------------------------------- */}

      <FAQS
        items={t("items", {
          ns: "faq",
          returnObjects: true,
        })}
      />

      {/* ----------------------------------------------------------------------------- */}

      <Section id="quick-help">
        <QuickHelpComponent
          title={t("quickHelp.title", { ns: "home" })}
          content={t("quickHelp.content", { ns: "home" })}
          image={besprechung}
          containedButton={t("to-the-contact-form", { ns: "common" })}
          containedLink="/contact"
          phone={t("address.tel", { ns: "contact" })}
          callButton={t("call", { ns: "common" })}
        />
      </Section>

      {/* ----------------------------------------------------------------------------- */}
    </Page>
  );
};

Home.getLayout = getLayout;

export const getStaticProps: GetStaticProps = async ({ locale = "" }) => ({
  props: {
    ...(await getServerTranslations(locale, ["common", "home", "faq", "service", "contact"])),
  },
});

export default Home;
